/**
 * Software (CPU) height-field ray marcher.
 *
 * This is the fallback renderer for machines without WebGPU, and it doubles as
 * the reference for the WGSL version: same traversal, same shading, so the two
 * can be compared pixel for pixel while debugging.
 *
 * Why ray march instead of a mesh: the simulation lives in a regular grid, so a
 * height field can be intersected directly and the level of detail adapts on its
 * own. A triangle mesh at 1024^2 would be two million triangles rebuilt whenever
 * the terrain changed, which is exactly the coupling the architecture is trying
 * to avoid.
 *
 * The traversal walks the ray in fixed steps, refines the first crossing with a
 * short binary search, then shades the hit with the same hillshade model for
 * every display mode.
 */

import { F, STATE_STRIDE } from '../simulation/constants.ts';
import { cameraBasis, type CameraState } from './camera.ts';
import {
  RAMP_HEIGHT,
  RAMP_WATER,
  rampFor,
  type ColorRamp,
  type RGB,
  type ViewMode,
} from './viewModes.ts';

/**
 * Internal render resolution, kept low and upscaled: the terrain is smooth.
 *
 * The user sets this from the Info panel and it stays put. An earlier version
 * adjusted it automatically to hold a frame-time target; that was removed because
 * each change visibly rescaled the whole image, which is worse than a steady frame
 * rate is good.
 */
export const DEFAULT_INTERNAL_WIDTH = 260;

/**
 * Bounds on the internal raster width. Below the minimum the upscale becomes
 * visible and the saving is small; above the maximum the frame time is no longer
 * defensible on the CPU.
 */
export const MIN_INTERNAL_WIDTH = 140;
export const MAX_INTERNAL_WIDTH = 480;

/**
 * Bisection steps once a cell segment has a bracketed crossing. Seven halves the
 * segment to under 1% of its length, which is finer than one pixel.
 */
const REFINE_STEPS = 7;

/**
 * Fixed substeps inside a cell before testing the surface directly.
 *
 * The cell ceiling is only an upper bound, so a cell that passes the ceiling test
 * still has to be walked to find the crossing. Four substeps keep the surface
 * detail inside a cell (the whole point of bilinear interpolation) rather than
 * flattening it to a corner-to-corner ramp, at a cost of one sample each.
 */
const SUBSTEPS_PER_CELL = 4;

/**
 * Cells per side of a coarse block in the traversal hierarchy.
 *
 * Measured: at the shipping camera a ray crosses 134 cells on average but ends up
 * testing only 1.7 of them — the rest are open air that the per-cell bound can
 * reject but the per-cell *walk* still has to step through one cell at a time. A
 * coarse level turns those ~134 steps into a small number of coarse steps. An
 * 8 x 8 block measured best across the raster, camera, zoom and grid-size sweep;
 * 16 x 16 made the bound too loose and descended into too many individual cells.
 */
const BLOCK_SHIFT = 3;
const BLOCK_SIZE = 1 << BLOCK_SHIFT;

/** Blocks per side of the coarse level, for a grid of `gw` vertices. */
function blockCount(gw: number): number {
  return Math.max(1, Math.ceil((gw - 1) / BLOCK_SIZE));
}

/** Sun direction, fixed in world space to match the WebGPU pass. */
const SUN: RGB = [-0.55, 0.72, -0.42];

export interface RenderOptions {
  mode: ViewMode;
  /** Elevation of the tallest cell, used to normalise the height field. */
  maxHeight: number;
  /** World-space extent of the grid in metres, used for the slope scaling. */
  worldExtent: number;
  showWater: boolean;
  /** Multiplies every shading term; drives the brightness control. */
  exposure: number;
  /** Overlay a grid at this many cells; 0 disables it. */
  gridStep: number;
}

export interface FrameStats {
  /** Milliseconds spent inside `render`. */
  renderMs: number;
  /** Internal raster resolution actually used. */
  width: number;
  height: number;
}

export class SoftwareTerrainRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  /** Owned pixel buffer; image is reallocated when the aspect changes. */
  private image: ImageData;
  /** Scratch height field, recreated only when the grid resolution changes. */
  private height!: Float32Array;
  private water!: Float32Array;
  private sediment!: Float32Array;
  private speed!: Float32Array;
  private erosion!: Float32Array;
  private hardness!: Float32Array;
  private soil!: Float32Array;
  private flowAccum!: Float32Array;
  /**
   * Per-cell ceiling: `max(height[i][j], height[i+1][j], height[i][j+1],
   * height[i+1][j+1])`. Bilinear interpolation never exceeds its corner values,
   * so this bounds the surface everywhere inside cell (i, j) and lets the march
   * skip cells the ray flies over. Indexed like `height` (`j * gw + i`), one
   * entry per cell; the last row and column are unused.
   */
  private maxHeight2x2!: Float32Array;
  /**
   * Coarse ceiling: the maximum of `maxHeight2x2` over a `BLOCK_SIZE^2` block of
   * cells. Built from the cell ceilings rather than from `height` directly, which
   * makes it a bound on the same quantity the cell level tests — so a ray that
   * clears the block bound clears every cell bound inside it, by construction.
   */
  private maxHeightBlock!: Float32Array;
  /** Blocks per side, matching `maxHeightBlock`. */
  private blockSide = 0;
  /** Reused hit coordinates; the hot path must not allocate a tuple per pixel. */
  private hitX = 0;
  private hitY = 0;
  private hitZ = 0;
  /** Shared colour output: ramp sampling happens serially inside the pixel loop. */
  private readonly colourScratch: RGB = [0, 0, 0];
  private activeRamp: ColorRamp = RAMP_HEIGHT;
  /** Frame constants used by every shaded hit. */
  private invMaxHeight = 1;
  private gradientScale = 1;
  /** Internal raster width. Mutable: see `setInternalWidth`. */
  private internalWidth: number;

  private lastStats: FrameStats = { renderMs: 0, width: 0, height: 0 };

  constructor(canvas: HTMLCanvasElement, internalWidth = DEFAULT_INTERNAL_WIDTH) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
    this.internalWidth = internalWidth;
    const aspect = canvas.clientHeight > 0 ? canvas.clientWidth / canvas.clientHeight : 16 / 9;
    const h = Math.max(1, Math.round(internalWidth / aspect));
    this.image = ctx.createImageData(internalWidth, h);
    canvas.width = internalWidth;
    canvas.height = h;
  }

  /** The raster width currently in use, which adaptive quality may have moved. */
  get width(): number {
    return this.internalWidth;
  }

  /**
   * Change the internal raster width.
   *
   * This is the renderer's only quality dial and it is deliberately cheap: the
   * canvas is resized and upscaled by the browser, so a lower width costs
   * resolution and nothing else. It does **not** force a reallocation, because
   * adaptive quality calls it from a live frame loop.
   */
  setInternalWidth(width: number): void {
    const w = Math.max(MIN_INTERNAL_WIDTH, Math.min(MAX_INTERNAL_WIDTH, Math.round(width)));
    if (w === this.internalWidth) return;
    this.internalWidth = w;
    this.resize();
  }

  /** Resize the internal raster to match the element's aspect ratio. */
  resize(): void {
    const aspect =
      this.canvas.clientHeight > 0 ? this.canvas.clientWidth / this.canvas.clientHeight : 16 / 9;
    const w = this.internalWidth;
    const h = Math.max(1, Math.round(w / aspect));
    if (h === this.image.height && w === this.image.width) return;
    // `ImageData` dimensions are read-only, so the buffer is replaced.
    this.image = this.ctx.createImageData(w, h);
    this.canvas.width = w;
    this.canvas.height = h;
  }

  private ensureBuffers(gridWidth: number): void {
    if (this.height && this.height.length === gridWidth * gridWidth) return;
    const n = gridWidth * gridWidth;
    this.height = new Float32Array(n);
    this.water = new Float32Array(n);
    this.sediment = new Float32Array(n);
    this.speed = new Float32Array(n);
    this.erosion = new Float32Array(n);
    this.hardness = new Float32Array(n);
    this.soil = new Float32Array(n);
    this.flowAccum = new Float32Array(n);
    this.maxHeight2x2 = new Float32Array(n);
    this.blockSide = blockCount(gridWidth);
    this.maxHeightBlock = new Float32Array(this.blockSide * this.blockSide);
  }

  /**
   * De-interleave the packed simulation state into the planes this frame reads.
   *
   * Height is always needed for traversal and normals. The other six planes are
   * mode-dependent; decoding all of them every frame was especially expensive
   * for velocity because it performed one square root for every simulation cell
   * even while another view was selected.
   */
  private upload(state: Float32Array, gridWidth: number, opts: RenderOptions): void {
    this.ensureBuffers(gridWidth);
    const n = gridWidth * gridWidth;
    const gw = gridWidth;
    const needsWater = opts.showWater || opts.mode === 'water';
    const needsSediment = opts.mode === 'sediment';
    const needsSpeed = opts.mode === 'velocity';
    const needsErosion = opts.mode === 'erosion' || opts.mode === 'deposition';
    const needsHardness = opts.mode === 'hardness';
    const needsSoil = opts.mode === 'terrain' || opts.mode === 'soil';
    for (let k = 0; k < n; k++) {
      const b = k * STATE_STRIDE;
      this.height[k] = state[b + F.height];
      if (needsWater) {
        this.water[k] = state[b + F.water];
        this.flowAccum[k] = state[b + F.flowAccum];
      }
      if (needsSediment) this.sediment[k] = state[b + F.sediment];
      if (needsSpeed) {
        const u = state[b + F.velU];
        const v = state[b + F.velV];
        this.speed[k] = Math.sqrt(u * u + v * v);
      }
      if (needsErosion) this.erosion[k] = state[b + F.erosion] - state[b + F.deposition];
      if (needsHardness) this.hardness[k] = state[b + F.hardness];
      if (needsSoil) this.soil[k] = state[b + F.soil];
    }

    // Build both hierarchy levels in one block-major pass. The old version first
    // wrote every cell ceiling and then scanned the entire plane again to fold it
    // into blocks. Keeping the block maximum in a local removes that third pass
    // and improves locality for large grids.
    const maxCell = this.maxHeight2x2;
    const nb = this.blockSide;
    const blockBound = this.maxHeightBlock;
    for (let bj = 0; bj < nb; bj++) {
      const cellJLo = bj * BLOCK_SIZE;
      const cellJHi = Math.min(cellJLo + BLOCK_SIZE, gw - 1);
      for (let bi = 0; bi < nb; bi++) {
        const cellILo = bi * BLOCK_SIZE;
        const cellIHi = Math.min(cellILo + BLOCK_SIZE, gw - 1);
        let m = 0;
        for (let cellJ = cellJLo; cellJ < cellJHi; cellJ++) {
          const row = cellJ * gw;
          const next = row + gw;
          for (let cellI = cellILo; cellI < cellIHi; cellI++) {
            const k = row + cellI;
            const a = this.height[k];
            const b = this.height[k + 1];
            const c = this.height[next + cellI];
            const d = this.height[next + cellI + 1];
            let ceiling = a > b ? a : b;
            if (c > ceiling) ceiling = c;
            if (d > ceiling) ceiling = d;
            maxCell[k] = ceiling;
            if (ceiling > m) m = ceiling;
          }
        }
        blockBound[bj * nb + bi] = m;
      }
    }
  }

  /**
   * Sample a plane with bilinear interpolation at grid coordinates, clamped to
   * the border. `u`/`v` are in cell units.
   */
  private sample(plane: Float32Array, gw: number, u: number, v: number): number {
    const x = u < 0 ? 0 : u > gw - 1.0001 ? gw - 1.0001 : u;
    const y = v < 0 ? 0 : v > gw - 1.0001 ? gw - 1.0001 : v;
    const x0 = x | 0;
    const y0 = y | 0;
    const x1 = x0 + 1 < gw ? x0 + 1 : x0;
    const y1 = y0 + 1 < gw ? y0 + 1 : y0;
    const tx = x - x0;
    const ty = y - y0;
    const a = plane[y0 * gw + x0];
    const b = plane[y0 * gw + x1];
    const c = plane[y1 * gw + x0];
    const d = plane[y1 * gw + x1];
    return (a + (b - a) * tx) * (1 - ty) + (c + (d - c) * tx) * ty;
  }



  /**
   * March one ray. Returns the hit point in normalised world space (x, z in
   * [0,1], y the normalised elevation) or null when the ray leaves the volume.
   *
   * ## Why a bounded walk and not a fixed-step walk
   *
   * The obvious implementation marches a fixed number of equal steps across the
   * clipped span and bisects where the sign flips. It is simple and it works, but
   * it is also blind: the step size has to be small enough for the *worst* ray in
   * the frame, so every ray pays for the shallowest approach any ray makes. On a
   * framed terrain that is mostly wasted — measured on the shipping camera, only
   * ~44% of rays hit anything at all. Worse, the fixed step is not even safe: at
   * 112 steps a ray skipping over a ridge between two samples *misses* it, which
   * the old renderer did on 0.6% of rays (see `scripts/marchAccuracy.ts`).
   *
   * ## The hierarchy
   *
   * Two levels, both of which are upper bounds on the surface underneath them,
   * which is what makes them safe to skip:
   *
   * - **Block** (`maxHeightBlock`) — the maximum over a `BLOCK_SIZE^2` group of
   *   cells. Bilinear interpolation never exceeds its corner values, so this
   *   bounds the surface everywhere in the block, by construction.
   * - **Cell** (`maxHeight2x2`) — the maximum of a cell's four corner heights,
   *   which bounds the bilinear surface inside that single cell.
   *
   * A ray segment whose *lowest* point is still above a bound cannot cross the
   * surface within that region, so the walk advances to the region's exit without
   * taking a single bilinear sample. Only a block that fails its test is walked
   * cell by cell, and only a cell that fails its test is sampled.
   *
   * The measured effect: a ray steps over ~134 cells' worth of distance using ~9
   * block steps instead, and samples the surface ~6 times. A fixed-step march
   * could never do this, because its step size is a property of the frame rather
   * than of the terrain under the ray.
   *
   * A min/max pyramid was tried here once before and measured slower. That version
   * kept the fixed fine step and merely consulted the pyramid to skip ahead
   * occasionally, so the coarse traversal was pure overhead bolted onto the fine
   * one. Here the coarse level *is* the traversal, and the fine level is entered
   * only where it can matter.
   */
  private march(
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    gw: number,
    heightScale: number,
  ): boolean {
    // Clip the ray to the box [0,1] x [-0.05, 1.35] x [0,1] with a standard slab
    // test. All three slabs are solved before the interval is validated: a
    // version that short-circuits between slabs compares a half-updated `tMax`
    // against a half-updated `tMin`, which silently produces an empty interval
    // for most camera angles.
    let tMin = 0;
    let tMax = Infinity;
    {
      const xLo = (0 - ox) / dx;
      const xHi = (1 - ox) / dx;
      if (xLo < xHi) {
        if (xLo > tMin) tMin = xLo;
        if (xHi < tMax) tMax = xHi;
      } else {
        if (xHi > tMin) tMin = xHi;
        if (xLo < tMax) tMax = xLo;
      }
      const zLo = (0 - oz) / dz;
      const zHi = (1 - oz) / dz;
      if (zLo < zHi) {
        if (zLo > tMin) tMin = zLo;
        if (zHi < tMax) tMax = zHi;
      } else {
        if (zHi > tMin) tMin = zHi;
        if (zLo < tMax) tMax = zLo;
      }
      const yLo = (-0.05 - oy) / dy;
      const yHi = (1.35 - oy) / dy;
      if (yLo < yHi) {
        if (yLo > tMin) tMin = yLo;
        if (yHi < tMax) tMax = yHi;
      } else {
        if (yHi > tMin) tMin = yHi;
        if (yLo < tMax) tMax = yLo;
      }
    }
    // `!(tMax > tMin)` is also what rejects the NaN a 0/0 direction produces for
    // an axis the ray is exactly parallel to and sits outside of.
    if (!(tMax > tMin) || !Number.isFinite(tMax)) return false;

    const invCell = gw - 1;
    const nb = blockCount(gw);
    // Block-space coordinates: one block unit is BLOCK_SIZE cells.
    const invBlock = invCell / BLOCK_SIZE;
    const blockBound = this.maxHeightBlock;

    let blockI = Math.floor((ox + dx * tMin) * invBlock);
    let blockJ = Math.floor((oz + dz * tMin) * invBlock);
    if (blockI < 0) blockI = 0;
    else if (blockI > nb - 1) blockI = nb - 1;
    if (blockJ < 0) blockJ = 0;
    else if (blockJ > nb - 1) blockJ = nb - 1;

    const stepBlockI = dx > 0 ? 1 : dx < 0 ? -1 : 0;
    const stepBlockJ = dz > 0 ? 1 : dz < 0 ? -1 : 0;
    const gbx = (ox + dx * tMin) * invBlock;
    const gbz = (oz + dz * tMin) * invBlock;
    let tNextBlockX =
      stepBlockI === 0 ? Infinity : tMin + (blockI + (stepBlockI > 0 ? 1 : 0) - gbx) / (dx * invBlock);
    let tNextBlockZ =
      stepBlockJ === 0 ? Infinity : tMin + (blockJ + (stepBlockJ > 0 ? 1 : 0) - gbz) / (dz * invBlock);
    const tDeltaBlockX = stepBlockI === 0 ? Infinity : 1 / Math.abs(dx * invBlock);
    const tDeltaBlockZ = stepBlockJ === 0 ? Infinity : 1 / Math.abs(dz * invBlock);

    const stepCellI = dx > 0 ? 1 : dx < 0 ? -1 : 0;
    const stepCellJ = dz > 0 ? 1 : dz < 0 ? -1 : 0;

    let sPrev = tMin;
    let rayPrev = oy + dy * sPrev;

    while (sPrev < tMax) {
      const sExitX = tNextBlockX < tMax ? tNextBlockX : tMax;
      const sExitZ = tNextBlockZ < tMax ? tNextBlockZ : tMax;
      const sExit = sExitX < sExitZ ? sExitX : sExitZ;
      const rayExit = oy + dy * sExit;
      const rayLow = rayExit < rayPrev ? rayExit : rayPrev;
      const blockCeiling = blockBound[blockJ * nb + blockI] * heightScale;

      if (rayLow <= blockCeiling) {
        // This block may hold the surface. Walk its cells, then resume the block
        // walk at this block's exit — `sExit` is exactly where it ends.
        const hit = this.walkCells(
          sPrev,
          sExit,
          rayPrev,
          ox, oy, oz, dx, dy, dz,
          gw, invCell, heightScale,
          stepCellI, stepCellJ,
        );
        if (hit) return true;
      }

      // Advance exactly one block along whichever axis boundary comes first. The
      // other axis catches up in a later iteration rather than being moved twice
      // in one step, which keeps the ceiling lookup conservative.
      if (sExitX <= sExitZ) {
        blockI += stepBlockI;
        tNextBlockX += tDeltaBlockX;
      } else {
        blockJ += stepBlockJ;
        tNextBlockZ += tDeltaBlockZ;
      }
      if (blockI < 0 || blockI >= nb || blockJ < 0 || blockJ >= nb) break;

      sPrev = sExit;
      rayPrev = rayExit;
    }
    return false;
  }

  /**
   * Walk the cells between two points along the ray, looking for a crossing.
   *
   * Both `sStart` and `sEnd` are supplied by the caller rather than derived from
   * the grid, which is what lets the block walk above clip the span exactly: the
   * DDA is re-derived per block from the segment's own entry point, so a cell is
   * never visited twice and none is skipped.
   */
  private walkCells(
    sStart: number,
    sEnd: number,
    rayStart: number,
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    gw: number,
    invCell: number,
    heightScale: number,
    stepI: number,
    stepJ: number,
  ): boolean {
    const maxCell = this.maxHeight2x2;

    let cellI = Math.floor((ox + dx * sStart) * invCell);
    let cellJ = Math.floor((oz + dz * sStart) * invCell);
    if (cellI < 0) cellI = 0;
    else if (cellI > invCell - 1) cellI = invCell - 1;
    if (cellJ < 0) cellJ = 0;
    else if (cellJ > invCell - 1) cellJ = invCell - 1;

    // Cell-local distance to the next boundary, then the per-cell stride. An
    // infinite stride for a zero component parks that axis, which is correct.
    const gx = (ox + dx * sStart) * invCell;
    const gz = (oz + dz * sStart) * invCell;
    let tNextX = stepI === 0 ? Infinity : sStart + (cellI + (stepI > 0 ? 1 : 0) - gx) / (dx * invCell);
    let tNextZ = stepJ === 0 ? Infinity : sStart + (cellJ + (stepJ > 0 ? 1 : 0) - gz) / (dz * invCell);
    const tDeltaX = stepI === 0 ? Infinity : 1 / Math.abs(dx * invCell);
    const tDeltaZ = stepJ === 0 ? Infinity : 1 / Math.abs(dz * invCell);

    let sPrev = sStart;
    let rayPrev = rayStart;

    while (sPrev < sEnd) {
      const sNextX = tNextX < sEnd ? tNextX : sEnd;
      const sNextZ = tNextZ < sEnd ? tNextZ : sEnd;
      const sNext = sNextX < sNextZ ? sNextX : sNextZ;

      const rayNext = oy + dy * sNext;
      // Lowest point of the ray segment. The ray is linear, so this is exact.
      const rayLow = rayNext < rayPrev ? rayNext : rayPrev;
      const ceiling = maxCell[cellJ * gw + cellI] * heightScale;

      if (rayLow <= ceiling) {
        // The segment may cross. Cheap fixed substeps to bracket the sign flip,
        // then bisect — the surface is bilinear inside the cell, so a sign change
        // is a genuine crossing and bisection cannot drift to a different one.
        const segLen = sNext - sPrev;
        let prevT = sPrev;
        let prevHeight = rayPrev;

        for (let k = 1; k <= SUBSTEPS_PER_CELL; k++) {
          const t = sPrev + (segLen * k) / SUBSTEPS_PER_CELL;
          const px = ox + dx * t;
          const pz = oz + dz * t;
          const height = this.sample(this.height, gw, px * invCell, pz * invCell) * heightScale;
          if (oy + dy * t - height <= 0 && prevHeight > 0) {
            let lo = prevT;
            let hi = t;
            for (let i = 0; i < REFINE_STEPS; i++) {
              const mid = 0.5 * (lo + hi);
              const mh = this.sample(this.height, gw, (ox + dx * mid) * invCell, (oz + dz * mid) * invCell) * heightScale;
              if (oy + dy * mid - mh > 0) lo = mid;
              else hi = mid;
            }
            const hitT = 0.5 * (lo + hi);
            this.hitX = ox + dx * hitT;
            this.hitY = oy + dy * hitT;
            this.hitZ = oz + dz * hitT;
            return true;
          }
          prevT = t;
          prevHeight = oy + dy * t - height;
        }
      }

      // Advance exactly one cell along whichever axis boundary comes first.
      if (sNextX <= sNextZ) {
        cellI += stepI;
        tNextX += tDeltaX;
      } else {
        cellJ += stepJ;
        tNextZ += tDeltaZ;
      }
      if (cellI < 0 || cellI >= invCell || cellJ < 0 || cellJ >= invCell) break;

      sPrev = sNext;
      rayPrev = rayNext;
    }
    return false;
  }



  /** Shade one hit point into `out`, writing one RGBA pixel. */
  private shade(
    wx: number,
    wz: number,
    gw: number,
    opts: RenderOptions,
    out: Uint8ClampedArray,
    o: number,
  ): void {
    const u = wx * (gw - 1);
    const v = wz * (gw - 1);

    // Central-difference normal.
    //
    // The gradient has to be the *physical* slope: a rise of `dh` metres over a
    // run of `cellSize` metres. The renderer holds terrain normalised by the peak
    // and spread over a unit square, so the height difference must be scaled back
    // by `peak / worldExtent` before dividing by the normalised cell width —
    // otherwise a 30-degree hillside reports a slope of 300 and the hillshade
    // saturates into a flat wash with no relief in it.
    const hL = this.sample(this.height, gw, u - 1, v);
    const hR = this.sample(this.height, gw, u + 1, v);
    const hD = this.sample(this.height, gw, u, v - 1);
    const hU = this.sample(this.height, gw, u, v + 1);
    const dex = (hR - hL) * this.gradientScale;
    const dey = (hU - hD) * this.gradientScale;
    let nx = -dex;
    let ny = 1;
    let nz = -dey;
    const nl = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    nx /= nl;
    ny /= nl;
    nz /= nl;

    /** Slope as a tangent (rise over run), which is what shading wants. */
    const slope = Math.sqrt(dex * dex + dey * dey);

    let r: number;
    let g: number;
    let b: number;

    if (opts.mode === 'terrain') {
      // Relief shading plus a regolith tint: bare rock reads greyer, deep soil
      // warmer, which is what makes an eroding ridge legible at a glance.
      const elev = this.sample(this.height, gw, u, v) * this.invMaxHeight;
      const base = RAMP_HEIGHT.into(elev, this.colourScratch);
      const soil = this.sample(this.soil, gw, u, v);
      const rock = 1 / (1 + soil * 0.5);
      r = base[0] * rock + 128 * (1 - rock);
      g = base[1] * rock + 120 * (1 - rock);
      b = base[2] * rock + 108 * (1 - rock);
      // Hillshade: Lambert plus a soft sky term, no specular. The ambient floor is
      // deliberately high — a scientific relief needs shadowed faces to stay
      // readable, and a low floor turns half the map into a black slab.
      const diff = Math.max(0, nx * SUN[0] + ny * SUN[1] + nz * SUN[2]);
      const sky = 0.5 + 0.5 * ny;
      const light = 0.52 + 0.55 * (0.70 * diff + 0.30 * sky);
      r *= light;
      g *= light;
      b *= light;
    } else {
      let value = 0;
      switch (opts.mode) {
        case 'height':
          value = this.sample(this.height, gw, u, v) * this.invMaxHeight;
          break;
        case 'slope':
          value = Math.min(1, slope * 1.6);
          break;
        case 'water':
          value = Math.min(1, this.sample(this.water, gw, u, v) / 4);
          break;
        case 'sediment':
          value = Math.min(1, this.sample(this.sediment, gw, u, v) / 0.5);
          break;
        case 'velocity':
          value = Math.min(1, this.sample(this.speed, gw, u, v) / 6);
          break;
        case 'erosion': {
          const e = this.sample(this.erosion, gw, u, v);
          value = 0.5 + Math.max(-0.5, Math.min(0.5, e * 1.2));
          break;
        }
        case 'deposition': {
          const e = this.sample(this.erosion, gw, u, v);
          value = 0.5 + Math.max(-0.5, Math.min(0.5, -e * 1.2));
          break;
        }
        case 'hardness':
          value = Math.min(1, this.sample(this.hardness, gw, u, v) / 3);
          break;
        case 'soil':
          value = Math.min(1, this.sample(this.soil, gw, u, v) / 6);
          break;
        default:
          value = 0;
      }
      const c = this.activeRamp.into(value, this.colourScratch);
      // A gentle hillshade on top of the ramp, so relief is still readable
      // without competing with the data colour.
      const diff = Math.max(0, nx * SUN[0] + ny * SUN[1] + nz * SUN[2]);
      const light = 0.62 + 0.38 * diff;
      r = c[0] * light;
      g = c[1] * light;
      b = c[2] * light;
    }

    // Water layer: a translucent shell over the terrain, tinted by depth and
    // slightly reflective at grazing angles. Rain is applied to every cell, so
    // a very shallow film eventually covers most of the map. Do not turn that
    // film into an opaque blue carpet: the old hard 1 cm threshold jumped from
    // zero to 22% opacity and made the whole mountain appear flooded at once.
    // Fade smoothly from 1–10 cm, while genuinely pooled water remains obvious.
    // In `water` mode the ramp already encodes depth, so the shell is suppressed
    // to keep the data readable.
    if (opts.showWater && opts.mode !== 'water') {
      const depth = this.sample(this.water, gw, u, v);
      const accumulation = this.sample(this.flowAccum, gw, u, v);
      const channel = Math.min(1, Math.max(0, Math.log2(Math.max(1, accumulation)) / 8));
      const start = 0.02 + (0.004 - 0.02) * channel;
      const end = 0.1 + (0.025 - 0.1) * channel;
      if (depth > start) {
        const deep = Math.min(1, depth / 3);
        const onset = Math.min(1, Math.max(0, (depth - start) / Math.max(1e-6, end - start)));
        const coverage = onset * onset * (3 - 2 * onset);
        const alpha = coverage * Math.min(0.82, 0.12 + 0.7 * Math.sqrt(deep));
        const wc = RAMP_WATER.into(deep, this.colourScratch);
        r = r * (1 - alpha) + wc[0] * alpha;
        g = g * (1 - alpha) + wc[1] * alpha;
        b = b * (1 - alpha) + wc[2] * alpha;
      }
    }

    // Optional grid overlay for scale reference.
    if (opts.gridStep > 0) {
      const gu = u / opts.gridStep;
      const gv = v / opts.gridStep;
      const du = Math.abs(gu - Math.round(gu));
      const dv = Math.abs(gv - Math.round(gv));
      const w = 0.035;
      if (du < w || dv < w) {
        r = r * 0.88 + 40;
        g = g * 0.88 + 38;
        b = b * 0.88 + 34;
      }
    }

    const ex = opts.exposure;
    out[o] = r * ex;
    out[o + 1] = g * ex;
    out[o + 2] = b * ex;
    out[o + 3] = 255;
  }

  /**
   * March a single ray in the renderer's own space, for diagnostics.
   *
   * The traversal is deterministic and self-contained, so exposing it lets the
   * quality gate in `scripts/compareRender.ts` check the intersection against an
   * independent brute-force march, ray by ray and against ground truth. Nothing in
   * the application calls this; `render` and `pick` are the real entry points.
   *
   * Requires `render` (or `compareRender`) to have run at least once for the
   * current grid, since it reads the ceiling planes those calls build.
   */
  probeRay(
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    gridWidth: number,
    heightScale: number,
  ): [number, number, number] | null {
    if (!this.march(ox, oy, oz, dx, dy, dz, gridWidth, heightScale)) return null;
    return [this.hitX, this.hitY, this.hitZ];
  }

  /** Render one frame of the simulation state. */
  render(state: Float32Array, gridWidth: number, camera: CameraState, opts: RenderOptions): FrameStats {
    const t0 = performance.now();
    this.resize();
    this.upload(state, gridWidth, opts);
    const { width: iw, height: ih } = this.image;
    this.trace(this.image.data, iw, ih, gridWidth, camera, opts);
    this.ctx.putImageData(this.image, 0, 0);
    this.lastStats = { renderMs: performance.now() - t0, width: iw, height: ih };
    return this.lastStats;
  }

  /** Refresh only the height hierarchy used by brush picking, without tracing pixels. */
  preparePicking(state: Float32Array, gridWidth: number, opts: RenderOptions): void {
    this.upload(state, gridWidth, opts);
  }

  /**
   * Render into a caller-supplied raster instead of the canvas.
   *
   * Exists so the traversal can be checked against a previous implementation
   * pixel for pixel (`scripts/compareRender.ts`). Timings from this method are
   * comparable to `render` because it does the same work apart from the final
   * `putImageData`, which belongs to the browser anyway.
   */
  compareRender(
    state: Float32Array,
    gridWidth: number,
    camera: CameraState,
    opts: RenderOptions,
    out: ImageData,
  ): FrameStats {
    const t0 = performance.now();
    this.internalWidth = out.width;
    this.image = out;
    this.upload(state, gridWidth, opts);
    this.trace(out.data, out.width, out.height, gridWidth, camera, opts);
    this.lastStats = { renderMs: performance.now() - t0, width: out.width, height: out.height };
    return this.lastStats;
  }

  /** The per-pixel inner loop, shared by `render` and `compareRender`. */
  private trace(
    data: Uint8ClampedArray,
    iw: number,
    ih: number,
    gridWidth: number,
    camera: CameraState,
    opts: RenderOptions,
  ): void {
    const basis = cameraBasis(camera, iw / ih);

    // One convention, shared by the render pass, the pick function and the
    // camera: the renderer works in a **unit cube**. XZ spans [0, 1] and
    // elevation spans [0, 1] after dividing by the peak, so the camera target and
    // distance are pure geometry and never need to know about metres. Mixing a
    // metre-based camera with a normalised terrain is what previously left the
    // eye at ground level and made every ray miss the box.
    const heightScale = 1 / Math.max(1e-6, opts.maxHeight);
    this.invMaxHeight = heightScale;
    this.gradientScale =
      (opts.maxHeight * Math.max(1, gridWidth - 1)) / (2 * Math.max(1e-6, opts.worldExtent));
    this.activeRamp = rampFor(opts.mode);

    const ox = basis.eye[0];
    const oy = basis.eye[1];
    const oz = basis.eye[2];
    const stride = 4;
    // Hoisted out of the inner loop: these are the same three multiplications for
    // every pixel of a row, and the ray direction differs only in `ndcX`.
    const fx = basis.forward[0];
    const fy = basis.forward[1];
    const fz = basis.forward[2];
    const rx = basis.right[0] * basis.aspect * basis.halfTan;
    const ry = basis.right[1] * basis.aspect * basis.halfTan;
    const rz = basis.right[2] * basis.aspect * basis.halfTan;
    const ux = basis.up[0] * basis.halfTan;
    const uy = basis.up[1] * basis.halfTan;
    const uz = basis.up[2] * basis.halfTan;

    for (let py = 0; py < ih; py++) {
      const ndcY = 1 - (2 * (py + 0.5)) / ih;
      const rowX = fx + ux * ndcY;
      const rowY = fy + uy * ndcY;
      const rowZ = fz + uz * ndcY;
      // Sky gradient is constant along a row, so it is computed once.
      const skyT = Math.max(0, Math.min(1, (ndcY + 1) * 0.5));
      const skyR = 240 + 8 * skyT;
      const skyG = 237 + 10 * skyT;
      const skyB = 231 + 12 * skyT;
      for (let px = 0; px < iw; px++) {
        const ndcX = (2 * (px + 0.5)) / iw - 1;
        let dx = rowX + rx * ndcX;
        let dy = rowY + ry * ndcX;
        let dz = rowZ + rz * ndcX;
        const dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
        dx /= dl;
        dy /= dl;
        dz /= dl;

        const o = (py * iw + px) * stride;
        const hit = this.march(ox, oy, oz, dx, dy, dz, gridWidth, heightScale);
        if (!hit) {
          // Miss: either the horizon plane (a flat sea-level disc, so the terrain
          // stands on something instead of floating over the sky) or the sky
          // above it. Without the disc the relief reads as a detached object.
          const tGround = dy < -1e-6 ? -oy / dy : Infinity;
          if (Number.isFinite(tGround)) {
            const gx = ox + dx * tGround;
            const gz = oz + dz * tGround;
            if (gx > -0.8 && gx < 1.8 && gz > -0.8 && gz < 1.8) {
              // Distance fade towards the sky colour, so the disc has no visible
              // outer edge.
              const fade = Math.min(1, Math.max(0, 1 - Math.max(0, tGround - 1.0) / 2.6));
              const k = 0.30 + 0.70 * fade;
              data[o] = 206 * k + 240 * (1 - k);
              data[o + 1] = 202 * k + 237 * (1 - k);
              data[o + 2] = 192 * k + 231 * (1 - k);
              data[o + 3] = 255;
              continue;
            }
          }
          data[o] = skyR;
          data[o + 1] = skyG;
          data[o + 2] = skyB;
          data[o + 3] = 255;
          continue;
        }
        this.shade(this.hitX, this.hitZ, gridWidth, opts, data, o);
      }
    }
  }

  stats(): FrameStats {
    return this.lastStats;
  }

  /**
   * Intersect the terrain with the ray through a normalised viewport position.
   *
   * Implemented here rather than in the UI layer because it needs the planes and
   * the traversal that are already resident: re-deriving them in the viewport
   * would be a second implementation of the same intersection, and the brush
   * would then diverge from what is drawn.
   *
   * Returns grid coordinates `[u, v]` (fractions of the world width) plus the
   * elevation in **metres**, which is what a brush stroke needs. The renderer
   * works in the normalised cube, so the metre conversion happens here, at the
   * boundary.
   */
  pick(
    ndcX: number,
    ndcY: number,
    camera: CameraState,
    gridWidth: number,
    maxHeight: number,
  ): [number, number, number] | null {
    if (!this.height) return null;
    const basis = cameraBasis(camera, this.image.width / Math.max(1, this.image.height));
    const heightScale = 1 / Math.max(1e-6, maxHeight);
    let dx = basis.forward[0] + basis.right[0] * ndcX * basis.aspect * basis.halfTan + basis.up[0] * ndcY * basis.halfTan;
    let dy = basis.forward[1] + basis.right[1] * ndcX * basis.aspect * basis.halfTan + basis.up[1] * ndcY * basis.halfTan;
    let dz = basis.forward[2] + basis.right[2] * ndcX * basis.aspect * basis.halfTan + basis.up[2] * ndcY * basis.halfTan;
    const dl = Math.hypot(dx, dy, dz) || 1;
    dx /= dl;
    dy /= dl;
    dz /= dl;
    const hit = this.march(basis.eye[0], basis.eye[1], basis.eye[2], dx, dy, dz, gridWidth, heightScale);
    if (!hit) return null;
    return [this.hitX, this.hitY / heightScale, this.hitZ];
  }

  destroy(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
